import React from "react";
import Feature from "ol/Feature";
import { useCallback, useEffect, useRef } from "react";
import { Coordinate } from "ol/coordinate";
import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import { Polygon } from "ol/geom";
import { fromLonLat } from "ol/proj";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { useMap } from "../../hooks/incontext/useMap";
import { makeText } from "../../utils/object";
import { ANNOTATION_COLOR } from "../../constants/color";
import { Annotation } from ".";
import { Text } from "ol/style";
import { useInteractionEvent } from "../../hooks/incontext/useInteractionEvent";
import { Geometry } from "ol/geom";

export interface CustomRectangleProps extends Annotation {
  positions: Coordinate[][];
}

export const CustomRectangle = ({
  positions,
  color = "BLUE",
  properties = {},
  onClick,
  onHover,
  onLeave,
  zIndex = 0,
  children,
  opacity = 1,
  isDisabledSelection = false,
}: CustomRectangleProps) => {
  const map = useMap();
  const annotationRef = useRef<Feature<Polygon>>(
    new Feature(
      new Polygon(
        positions.map((pos) => pos.map((position) => fromLonLat(position)))
      )
    )
  );

  const annotationLayerRef = useRef<
    VectorLayer<VectorSource<Feature<Geometry>>>
  >(
    new VectorLayer({
      source: new VectorSource({
        wrapX: false,
        features: [annotationRef.current],
      }),
    })
  );

  const annotationStyleRef = useRef(
    new Style({
      stroke: new Stroke({
        color: ANNOTATION_COLOR[color].stroke(opacity),
        width: 2,
      }),
      fill: new Fill({
        color: ANNOTATION_COLOR[color].fill(opacity),
      }),
      text:
        children && !children.props.isPopup
          ? makeText({
              text: children.props.children || "",
              size: children.props.size || 15,
              color: children.props.color ? children.props.color : "black",
              outline: children.props.outline,
              isMarker: false,
            })
          : undefined,
    })
  );

  const onHoverHandler = useCallback(
    (feature: Feature) => {
      if (feature) {
        if (onHover) {
          onHover({ annotation: annotationRef.current, properties });
        }
      }

      // 수정중일땐 팝업 관여하지 않음
      if (map.getProperties().isModifying) return;

      // Pop up text
      if (feature && children?.props.isPopup) {
        const hoveredFeature = feature;
        const hoveredFeatureStyle = hoveredFeature.getStyle() as Style;
        hoveredFeatureStyle.setText(
          makeText({
            text: children.props.children || "",
            size: children.props.size || 15,
            color: children.props.color ? children.props.color : "black",
            outline: children.props.outline,
            isMarker: false,
          })
        );

        annotationRef.current.setStyle(hoveredFeatureStyle);
      } else if (feature && children?.props.isPopup) {
        const hoveredFeatureStyle = annotationRef.current.getStyle() as Style;

        hoveredFeatureStyle.setText(new Text());
        annotationRef.current.setStyle(hoveredFeatureStyle);
      }
    },
    [children, map, onHover, properties]
  );

  const onClickHandler = useCallback(() => {
    if (onClick) {
      onClick({
        annotation: annotationRef.current,
        properties,
      });
    }
  }, [onClick, properties]);

  useInteractionEvent({
    annotation: annotationLayerRef.current,
    onClick: onClickHandler,
    onHover: onHoverHandler,
    isDisabledSelection,
  });

  useEffect(() => {
    if (annotationRef.current) {
      const geometry = annotationRef.current.getGeometry() as Polygon;
      geometry.setCoordinates([
        positions[0].map((position) => fromLonLat(position)),
      ]);
    }
  }, [positions]);

  useEffect(() => {
    if (annotationLayerRef.current && zIndex) {
      annotationLayerRef.current.setZIndex(zIndex);
    }
  }, [zIndex]);

  useEffect(() => {
    if (annotationLayerRef.current && children) {
      annotationStyleRef.current.setText(
        makeText({
          text: children.props.children || "",
          size: children.props.size || 15,
          color: children.props.color ? children.props.color : "black",
          outline: children.props.outline,
          isMarker: false,
        })
      );
    }
  }, [children]);

  useEffect(() => {
    annotationStyleRef.current.setFill(
      new Fill({
        color: ANNOTATION_COLOR[color].fill(opacity),
      })
    );
    annotationStyleRef.current.setStroke(
      new Stroke({
        color: ANNOTATION_COLOR[color].stroke(opacity),
        width: 2,
      })
    );
  }, [opacity, color]);

  useEffect(() => {
    if (!children) {
      annotationStyleRef.current.setText(new Text());
    }
  }, [children]);

  // 레이어 생명주기 — map 변경 시에만 마운트/언마운트
  useEffect(() => {
    if (!map) return;
    const annotationLayerCurrent = annotationLayerRef.current;
    annotationRef.current.setStyle(annotationStyleRef.current);
    map.addLayer(annotationLayerCurrent);

    return () => {
      annotationLayerCurrent.getSource()?.clear();
      map.removeLayer(annotationLayerCurrent);
    };
  }, [map]);

  // 프로퍼티 업데이트
  useEffect(() => {
    annotationRef.current.setProperties({
      ...properties,
      shape: "Rectangle",
      isModifying: false,
      source: annotationLayerRef.current.getSource(),
      layer: annotationLayerRef.current,
      hasPopup: children ? children?.props.isPopup : false,
    });
  }, [properties, children]);

  return null;
};
